import { useEffect, useState } from 'react';
import {
  fetchProcurementLineRemarkHistory,
  fetchRecentProcurementRemarkHistory,
} from '../../api/procurement';
import { secondaryButtonClassName } from '../../components/ui';

function formatChangedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function describeChange(entry) {
  if (!entry.oldRemark && entry.newRemark) {
    return { kind: 'Added', detail: entry.newRemark };
  }
  if (entry.oldRemark && !entry.newRemark) {
    return { kind: 'Cleared', detail: entry.oldRemark };
  }
  return { kind: 'Updated', detail: entry.newRemark || '—' };
}

export default function RemarkHistoryModal({
  isOpen,
  onClose,
  token,
  lineId,
  orderId,
  title = 'Remark history',
}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !token || (!lineId && !orderId)) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setEntries([]);

    (async () => {
      try {
        const payload = lineId
          ? await fetchProcurementLineRemarkHistory(token, lineId)
          : await fetchRecentProcurementRemarkHistory(token, { orderId });
        if (!cancelled) {
          setEntries(payload.entries || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load remark history.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, lineId, orderId, token]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="remark-history-title"
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="remark-history-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {orderId && !lineId
                ? 'All remark edits for this order.'
                : 'All remark edits for this order or procurement line.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading history…</p>
        ) : error ? (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        ) : entries.length ? (
          <ul className="max-h-[24rem] space-y-3 overflow-y-auto pr-1">
            {entries.map((entry) => {
              const change = describeChange(entry);
              return (
                <li
                  key={entry.id}
                  className="rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2.5"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {change.kind}
                    </span>
                    <span className="text-xs text-slate-500">{formatChangedAt(entry.changedAt)}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-900 whitespace-pre-wrap">
                    {change.detail}
                  </p>
                  {change.kind === 'Updated' && entry.oldRemark ? (
                    <p className="mt-1 text-xs text-slate-500 line-through whitespace-pre-wrap">
                      {entry.oldRemark}
                    </p>
                  ) : null}
                  {entry.actorName ? (
                    <p className="mt-1.5 text-xs text-slate-500">by {entry.actorName}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No remark history yet.</p>
        )}

        <div className="mt-4 flex justify-end">
          <button type="button" className={secondaryButtonClassName} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function RemarkHistoryButton({ lineId, orderId, onClick, className = '' }) {
  const enabled = Boolean(lineId || orderId);
  return (
    <button
      type="button"
      disabled={!enabled}
      title={enabled ? 'View remark history' : 'Save the line first to view history'}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={[
        'shrink-0 text-xs font-semibold text-blue-700 hover:text-blue-800 disabled:cursor-not-allowed disabled:text-slate-400',
        className,
      ].join(' ')}
    >
      History
    </button>
  );
}
